import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

interface PreviousListSelectorProps {
  campaignId: string;
  onComplete: (data: any) => void;
  onBack?: () => void;
}

interface TargetLeadList {
  id: string;
  request_name: string;
  created_at: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  filters: any;
  target_lead_count?: number;
  priority?: string;
}

export function PreviousListSelector({ campaignId, onComplete, onBack }: PreviousListSelectorProps) {
  const [loading, setLoading] = useState(true);
  const [targetLeadLists, setTargetLeadLists] = useState<TargetLeadList[]>([]);
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTargetLeadLists();
  }, []);

  const fetchTargetLeadLists = async () => {
    try {
      setLoading(true);
      
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('No active session found');
        setLoading(false);
        return;
      }

      // Fetch previous target lead requests from preset_filter_requests table
      // Include all statuses: pending (not started), in_progress (CSR working), completed (CSR finished)
      const { data, error: fetchError } = await supabase
        .from('preset_filter_requests')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['pending', 'in_progress', 'completed', 'approved', 'processed'])
        .order('created_at', { ascending: false })
        .limit(20);

      if (fetchError) {
        console.error('Error fetching target lead lists:', fetchError);
        setError('Failed to fetch previous target lead lists');
      } else {
        setTargetLeadLists(data || []);
      }
    } catch (error) {
      console.error('Error fetching target lead lists:', error);
      setError('Failed to load previous lists. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedList) {
      alert('Please select a target lead list to use');
      return;
    }

    const selectedListData = targetLeadLists.find(list => list.id === selectedList);
    if (!selectedListData) {
      alert('Selected list not found');
      return;
    }

    // Just like PresetFilterForm, we don't update the campaign here
    // We just pass the selection back to the parent component
    setIsSubmitted(true);
    onComplete({
      type: 'past_list',
      data: {
        list_id: selectedList,
        list_name: selectedListData.request_name,
        shouldAdvance: true
      }
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getFilterSummary = (filters: any) => {
    if (!filters) return '';
    
    const summaryParts = [];
    
    // Extract key filter information
    if (filters.location) {
      if (filters.location.locations?.length > 0) {
        summaryParts.push(filters.location.locations[0]);
      } else if (filters.location.countries?.length > 0) {
        summaryParts.push(filters.location.countries[0]);
      }
    }
    
    if (filters.industry) {
      if (filters.industry.industries?.length > 0) {
        summaryParts.push(filters.industry.industries[0]);
      }
    }
    
    if (filters.position) {
      if (filters.position.titles) {
        summaryParts.push(filters.position.titles);
      } else if (filters.position.seniority?.length > 0) {
        summaryParts.push(filters.position.seniority[0]);
      }
    }

    // Also check for old filter structure (backward compatibility)
    if (filters.geography?.locations?.length > 0) {
      summaryParts.push(filters.geography.locations[0]);
    }
    if (filters.company?.industries?.length > 0) {
      summaryParts.push(filters.company.industries[0]);
    }
    if (filters.role?.titles) {
      summaryParts.push(filters.role.titles);
    }
    
    // Limit to 3 items and add ellipsis if more
    const displayParts = summaryParts.slice(0, 3);
    if (summaryParts.length > 3) {
      displayParts.push('...');
    }
    
    return displayParts.join(', ');
  };

  if (loading && targetLeadLists.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={fetchTargetLeadLists} className="mt-2 text-red-600 underline">
          Try Again
        </button>
      </div>
    );
  }

  // Empty state - no previous target lead lists
  if (targetLeadLists.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Previous Target Lead Lists</h2>
          <p className="text-gray-600 mt-1">Select a target lead list from your previous requests</p>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 text-center">
          <div className="flex justify-center mb-3">
            <svg className="h-12 w-12 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-yellow-800 mb-2">No Previous Target Lead Lists</h3>
          <p className="text-yellow-700 mb-4">
            You haven't submitted any target lead requests yet.<br />
            Please go back and select a different lead acquisition method.
          </p>
          {onBack && (
            <button
              onClick={onBack}
              className="px-6 py-2 border border-yellow-600 rounded-md text-yellow-700 hover:bg-yellow-100"
            >
              ← Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Previous Target Lead Lists</h2>
        <p className="text-gray-600 mt-1">Select a target lead list from your previous requests</p>
      </div>

      {/* Target Lead List Cards */}
      <div className="space-y-3">
        {targetLeadLists.map((list) => (
          <div
            key={list.id}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedList === list.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
            } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !isSubmitted && setSelectedList(list.id)}
          >
            <div className="flex items-start">
              <input
                type="radio"
                name="target-lead-list"
                checked={selectedList === list.id}
                onChange={() => setSelectedList(list.id)}
                disabled={isSubmitted}
                className="mt-1 mr-3 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">{list.request_name}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    list.status === 'completed' ? 'bg-green-100 text-green-800' :
                    list.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                    list.status === 'processed' ? 'bg-purple-100 text-purple-800' :
                    list.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {list.status === 'completed' ? '✅' : 
                     list.status === 'approved' ? '✓' :
                     list.status === 'processed' ? '📋' :
                     list.status === 'in_progress' ? '🔄' :
                     '⏳'} {list.status.replace('_', ' ').split(' ').map(word => 
                       word.charAt(0).toUpperCase() + word.slice(1)
                     ).join(' ')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Created: {formatDate(list.created_at)}
                </p>
                {getFilterSummary(list.filters) && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Filters:</span> {getFilterSummary(list.filters)}
                    </p>
                  </div>
                )}
                {list.target_lead_count && (
                  <p className="text-xs text-gray-500 mt-1">
                    Target: {list.target_lead_count.toLocaleString()} leads
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Success Message */}
      {isSubmitted && selectedList && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Target Lead List Selected
              </h3>
              <p className="mt-1 text-sm text-green-700">
                <strong>{targetLeadLists.find(l => l.id === selectedList)?.request_name}</strong> will be used for this campaign.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between">
        {onBack && (
          <button
            onClick={onBack}
            disabled={isSubmitted}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Back
          </button>
        )}
        {!isSubmitted ? (
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedList}
            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
          >
            {loading ? 'Selecting List...' : 'Use Selected List'}
          </button>
        ) : (
          <button
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 ml-auto"
            onClick={() => {
              // This will be handled by the parent component
            }}
          >
            Proceed to Next Step →
          </button>
        )}
      </div>
    </div>
  );
}